import { createAction, createAsyncThunk } from "@reduxjs/toolkit";

import {
  ADD_DEPOT_ROUTE,
  GET_DEPOT_ROUTES,
  REMOVE_DEPOT_ROUTE,
  UPDATE_DEPOT_ROUTE,
} from "../../consts/apis";
import {
  SUCCESS_FETCH_DEPOT_ROUTES,
  ERROR_FETCH_DEPOT_ROUTES,
} from "../../consts/messages";

import api from "../../services/api";

import { generateColor } from "../../utils/routes";

import { addNotification, setSelectedRoute } from "../ownApp";
import { routeEditSelector, routeSelector, routesSelector } from "./selectors";
import {
  depotSessionChecksumSelector,
  depotChecksumSelector,
} from "../depot/selectors";
import { setDepotChecksum } from "../depot/thunkActions";
import { getSelectedDepot } from "../ownApp/selectors";

export const resetRouteManagement = createAsyncThunk(
  "routeManagement/resetRouteManagement",
  async (params) => ({})
);

export const fetchDepotRoutes = createAsyncThunk(
  "routeManagement/fetchDepotRoutes",
  async ({ depotCode }, { dispatch }) => {
    try {
      const {
        data: { maxAdditionalMinutes, routeConfig, routes },
      } = await api.get(GET_DEPOT_ROUTES(depotCode));

      dispatch(
        addNotification({
          message: SUCCESS_FETCH_DEPOT_ROUTES,
          options: { variant: "success" },
        })
      );

      dispatch(setSelectedRoute(null));

      return {
        maxAdditionalMinutes,
        routeConfig,
        routes: routes.map((route, index) => ({
          ...route,
          ...generateColor(index),
        })),
      };
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_FETCH_DEPOT_ROUTES,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const editRoute = createAsyncThunk(
  "routeManagement/editRoute",
  async ({ routeCode }, { dispatch, getState }) => {
    const state = getState();
    const route = routeSelector(routeCode)(state);
    dispatch(setSelectedRoute(null));
    return route;
  }
);

export const updateRouteEdit = createAsyncThunk(
  "routeManagement/updateRouteEdit",
  async ({ name, value }, { getState }) => {
    const state = getState();
    const routeEdit = routeEditSelector(state);
    return {
      ...routeEdit,
      [name]: value,
    };
  }
);

export const updateRouteEditRoadSpeed = createAsyncThunk(
  "routeManagement/updateRouteEditRoadSpeed",
  async ({ roadCode, name, value }, { getState }) => {
    const state = getState();
    const routeEdit = routeEditSelector(state);

    const [firstChar, secondChar] = value;
    if ((firstChar === "_") & !isNaN(+secondChar)) value = `0${secondChar}`;

    return {
      ...routeEdit,
      roadSpeeds: routeEdit.roadSpeeds.map((roadSpeed) => {
        if (roadSpeed.roadCode !== roadCode) return roadSpeed;
        else
          return {
            ...roadSpeed,
            [name]: value,
          };
      }),
    };
  }
);

export const addRoute = createAsyncThunk(
  "routeManagement/addRoute",
  async ({ route }, { dispatch, getState }) => {
    const state = getState();
    const depotCode = getSelectedDepot(state);
    const routes = routesSelector(state);

    try {
      const {
        data: { success },
      } = await api.post(ADD_DEPOT_ROUTE(depotCode), null, route);

      if (!success) throw new Error("Failed to remove depot route");

      dispatch(setSelectedRoute(route));

      dispatch(
        addNotification({
          message: `Successfully saved new route.`,
          options: { variant: "success" },
        })
      );

      return [...routes, route]
        .sort((routeA, routeB) =>
          routeA.routeCode.localeCompare(routeB.routeCode)
        )
        .map((route, index) => ({
          ...route,
          ...generateColor(index),
        }));
    } catch (error) {
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const saveRoute = createAsyncThunk(
  "routeManagement/saveRoute",
  async (params, { dispatch, getState }) => {
    const state = getState();
    const depotCode = getSelectedDepot(state);
    const routeEdit = routeEditSelector(state);
    const routes = routesSelector(state);
    try {
      const {
        data: { success },
      } = await api.put(UPDATE_DEPOT_ROUTE(depotCode), null, {
        routeCode: routeEdit.routeCode,
        additionalMinutes: routeEdit.additionalMinutes,
        useFerry: routeEdit.useFerry,
        useToll: routeEdit.useToll,
        roadSpeeds: routeEdit.roadSpeeds,
      });
      if (!success) throw new Error("Fail to update depot route");

      dispatch(
        addNotification({
          message: `Successfully updated route.`,
          options: { variant: "success" },
        })
      );

      return routes.map((route) =>
        route.routeCode !== routeEdit.routeCode ? route : routeEdit
      );
    } catch (error) {
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const removeRoute = createAsyncThunk(
  "routeManagement/removeRoute",
  async ({ routeCode }, { dispatch, getState }) => {
    const state = getState();
    const depotCode = getSelectedDepot(state);
    const depotChecksum = depotChecksumSelector(state);
    const sessionChecksum = depotSessionChecksumSelector(state);
    const routes = routesSelector(state);
    try {
      const {
        data: { success, depotChecksum: newDepotChecksum },
      } = await api.delete(REMOVE_DEPOT_ROUTE(depotCode, routeCode), null, {
        depotChecksum,
        sessionChecksum,
      });
      if (!success) throw new Error("Failed to remove depot route");

      dispatch(
        addNotification({
          message: `Successfully removed route.`,
          options: { variant: "success" },
        })
      );

      dispatch(setDepotChecksum(newDepotChecksum));

      return routes.filter((route) => route.routeCode !== routeCode);
    } catch (error) {
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      throw error;
    } finally {
      dispatch(cancelRouteEdit());
      dispatch(setSelectedRoute(null));
    }
  }
);

export const cancelRouteEdit = createAction("routeManagement/cancelRouteEdit");

export const setIsRouteEditValid = createAction(
  "routeManagement/setIsRouteEditValid"
);
